import useEstimateRenewal from "features/subscriptions/datahooks/useEstimateRenewal";

import Skeleton from "common/components/ui/Skeleton";
import type {
  AgencySubscription,
  RentedSubscription,
  Subscription,
} from "common/types";

import { formatBillingDate } from "../../utils";
import PlanSettingsDetailsPanel from "../PlanSettingsCard/PlanSettingsDetailsPanel";
import Price from "../Price";
import SubscriptionPaymentMethod from "./SubscriptionPaymentMethod";

interface SubscriptionBillingPanelProps {
  subscription:
    | Subscription
    | AgencySubscription
    | RentedSubscription
    | undefined;
}

export default function SubscriptionBillingPanel({
  subscription,
}: SubscriptionBillingPanelProps) {
  const { id, type, status, cancelled_at, item_price, has_changes } =
    subscription;

  const { estimateRenewal, isLoadingEstimateRenewal } = useEstimateRenewal(
    type === "agency" ? undefined : id,
  );

  let cardLabel =
    subscription.type === "rental" ? "Upcoming invoice" : "Next charge";
  let dateLabel = "Renews on";
  let nextBilling: number;

  if (estimateRenewal) {
    nextBilling = estimateRenewal.subscription_estimate.next_billing_at;
  }

  const isScheduledCancellation =
    status === "non_renewing" ||
    //this handles cancelled trial subscriptions, user only
    (type === "user" ? cancelled_at * 1000 > Date.now() : false);

  if (status === "in_trial") {
    cardLabel = "First charge";
  } else if (isScheduledCancellation) {
    dateLabel = "Ends on";
    cardLabel = "No upcoming charges";
    nextBilling = cancelled_at;
  }

  return (
    <PlanSettingsDetailsPanel className="p-5 gap-3 relative">
      <div className="flex flex-row justify-between gap-4 items-start">
        <div className="flex flex-col gap-4">
          <span className="text-xs-medium text-black-500">{cardLabel}</span>

          <div className="flex flex-col gap-1">
            <span className="text-xxs text-black-500">{dateLabel}</span>

            {isLoadingEstimateRenewal || !nextBilling ? (
              <Skeleton className="h-4 w-32" />
            ) : (
              <span className="text-xs-bold">
                {formatBillingDate(nextBilling * 1000)}
              </span>
            )}
          </div>
        </div>

        <Price
          estimate={estimateRenewal}
          pricingPeriodInfo={has_changes ? undefined : item_price}
          isScheduledCancellation={isScheduledCancellation}
          isLoading={!subscription || isLoadingEstimateRenewal}
        />
      </div>

      <div className="border-t-2 border-dotted border-black-200" />

      <div className="flex flex-col gap-4">
        <span className="text-xs-medium text-black-500">Payment</span>

        <SubscriptionPaymentMethod
          subscriptionId={subscription.id}
          isAgency={subscription.type === "agency"}
        />
      </div>
    </PlanSettingsDetailsPanel>
  );
}
